import View from 'app/views/view';
import GalleryPolicy from 'app/policies/galleryPolicy';

export default class GalleryView extends View {
  constructor(gallery) {
    super(gallery);
    this.policy = new GalleryPolicy(this);
  }

  pageNumber() {
    return this.searchParameters.page
      ? parseInt(this.searchParameters.page, 10)
      : 1;
  }

  hasResults() {
    return this.assets.length > 0;
  }

  isIstock() {
    return this.searchProfile.site.nickname === 'istock';
  }

  isMobile() {
    return this.searchProfile.isMobile;
  }

  isSubscriptionRepackagingActive() {
    return !!this.searchProfile.isSubscriptionRepackagingActive;
  }

  isBasicSubscriber() {
    return !!this.searchProfile.hasEssentialSubscription;
  }

  isBothFamilies() {
    return (
      !this.hasFamily() ||
      (this.searchParameters.family.includes('creative') &&
        this.searchParameters.family.includes('editorial'))
    );
  }

  isImageAndFilm() {
    return this.searchParameters.assettype === 'image,film';
  }

  // CREATIVE METHODS

  isCreative() {
    return this.isBothFamilies() || this.isCreativeOnly();
  }

  isCreativeOnly() {
    return this.searchParameters.family === 'creative';
  }

  isCreativeImageOnly() {
    return this.isCreativeOnly() && this.isImage() && !this.isSearchByImage();
  }

  isCreativeGeneral() {
    return (
      (this.isImage() || this.isImageAndFilm()) &&
      this.isCreativeOnly() &&
      !this.mediaType()
    );
  }

  isImage() {
    return this.searchParameters.assettype === 'image';
  }

  isFilmOrVideoTemplate() {
    return this.isVideoTemplate() || this.isFilm();
  }

  // CREATIVE - PHOTOGRAPHY METHODS

  isPhotography() {
    return this.mediaType() === 'photography';
  }

  // CREATIVE - ILLUSTRATION METHODS

  isIllustration() {
    return this.mediaType().includes('illustration');
  }

  isIllustrationOnly() {
    return (
      this.mediaType() === 'illustration' && this.assetFileType() !== 'eps'
    );
  }

  // CREATIVE - VECTOR METHODS

  isVector() {
    return this.isIllustration() && this.assetFileType() === 'eps';
  }

  // EDITORIAL METHODS

  isEditorial() {
    return this.isBothFamilies() || this.isEditorialOnly();
  }

  isEditorialOnly() {
    return this.searchParameters.family === 'editorial';
  }

  isEditorialImage() {
    return this.isEditorial() && this.isImage();
  }

  isEditorialImageOnly() {
    return this.isEditorialOnly() && this.isImage() && !this.isSearchByImage();
  }

  hasEditorialProduct() {
    return this.isFilm()
      ? this.hasEditorialFilmProduct()
      : this.hasEditorialImageProduct();
  }

  hasEditorialImageProduct() {
    return (
      this.isEditorialAll() ||
      this.isEditorialNews() ||
      this.isEditorialSports() ||
      this.isEditorialEntertainment() ||
      this.isEditorialFashion() ||
      this.isEditorialArchival()
    );
  }

  hasEditorialFilmProduct() {
    return (
      this.isEditorialNews() ||
      this.isEditorialSports() ||
      this.isEditorialEntertainment() ||
      this.isEditorialFashion() ||
      this.isEditorialArchival()
    );
  }

  isEditorialAll() {
    return this.searchParameters.editorialproducts === 'all';
  }

  isEditorialNews() {
    return this.searchParameters.editorialproducts === 'news';
  }

  isEditorialSports() {
    return this.searchParameters.editorialproducts === 'sport';
  }

  isEditorialEntertainment() {
    return this.searchParameters.editorialproducts === 'entertainment';
  }

  isEditorialArchival() {
    return this.searchParameters.editorialproducts === 'archival';
  }

  isEditorialFashion() {
    return this.searchParameters.editorialproducts === 'fashion';
  }

  // FILM METHODS
  isFilm() {
    return this.searchParameters.assettype === 'film';
  }

  isVideoTemplate() {
    return this.searchParameters.assettype === 'videotemplate';
  }

  isAllFilm() {
    return this.isFilm() && this.isBothFamilies();
  }

  isCreativeFilm() {
    return this.isFilm() && this.isCreativeOnly();
  }

  isCreativeFilmOnly() {
    return this.isCreativeFilm() && !this.isEditorialFilm();
  }

  isEditorialFilm() {
    return this.isFilm() && this.isEditorialOnly();
  }

  isEditorialFilmOnly() {
    return this.isEditorialFilm() && !this.isCreativeFilm();
  }

  // Search Parameter Methods

  isRecentlyViewedImages() {
    return !!this.searchParameters.recentlyviewed;
  }

  isGroupedByEvent() {
    return !!this.searchParameters.groupbyevent;
  }

  isStackSearch() {
    return !!this.searchParameters.stackid && this.isBlankPhrase();
  }

  isSearchByImage() {
    return !!this.searchParameters.searchbyimage;
  }

  isEvent() {
    return !!this.searchParameters.eventid;
  }

  isEvents() {
    const ev = this.searchParameters.events;
    return Array.isArray(ev) && ev.length > 0;
  }

  isPhotographerSearch() {
    return this.isImage() && !!this.searchParameters.artistexact;
  }

  isMoreLikeThis() {
    return !!this.searchParameters.morelikethis;
  }

  isSimilarSearch() {
    return this.isMoreLikeThis();
  }

  isCuratedSet() {
    return !!this.searchParameters.curatedset && this.isBlankPhrase();
  }

  isAssetIdSearch() {
    return this.isAssetIdCorrected();
  }

  isAssetIdCorrected() {
    return !!this.queryCorrections.asset_ids;
  }

  isFamilyCorrected() {
    return !!this.queryCorrections.family;
  }

  isGroupByEventCorrected() {
    return this.queryCorrections?.groupbyevent === false;
  }

  isArtist() {
    return !!this.searchParameters.artistexact;
  }

  isAvailableParameter(parameterName) {
    return this.searchParameters[parameterName] != null;
  }

  isSpecialSearch() {
    return (
      this.isAssetIdSearch() ||
      this.isStackSearch() ||
      this.isCuratedSet() ||
      this.isRecentlyViewedImages() ||
      this.isSearchByImage() ||
      this.isMoreLikeThis() ||
      this.isPortfolio() ||
      this.isArtist()
    );
  }

  isBlankPhrase() {
    return (
      !this.searchParameters.phrase || this.searchParameters.phrase.length === 0
    );
  }

  isPortfolio() {
    return !!this.searchParameters.contributor;
  }

  hasFamily() {
    return !!this.searchParameters.family;
  }

  isOfflineContentOnly() {
    return this.searchParameters.offlinecontent === 'exclusive';
  }

  assetFileType() {
    return this.searchParameters.assetfiletype;
  }

  mediaType() {
    const validTypes = ['illustration', 'photography'];
    const type = this.searchParameters.mediatype || '';
    if (validTypes.includes(type)) {
      return type;
    }
    return '';
  }

  isAllIstockCollections() {
    return !this.searchParameters.istockcollection;
  }

  isEssentialIstockCollections() {
    return this.searchParameters.istockcollection === 'main,value';
  }

  isSignatureIstockCollections() {
    return this.searchParameters.istockcollection === 'signature,signatureplus';
  }

  // Agreement Methods

  hasPremiumAccess() {
    return !!this.searchParameters.premiumaccess;
  }

  hasEasyAccess() {
    return !!this.searchParameters.easyaccess;
  }

  hasEditorialSubscription() {
    return !!this.searchParameters.editorialsubscription;
  }

  hasRoyaltyFreeSubscription() {
    return !!this.searchParameters.royaltyfreesubscription;
  }

  isFilteringByAgreement() {
    return (
      this.hasPremiumAccess() ||
      this.hasEasyAccess() ||
      this.hasEditorialSubscription() ||
      this.hasRoyaltyFreeSubscription()
    );
  }

  isPaPreview() {
    return this.searchProfile.isPaPreview;
  }

  isViewOnly() {
    if (this.searchParameters.premiumaccess) {
      const id = this.searchParameters.premiumaccess.split(':')[1];
      return (
        this.isPaAgreementViewOnly(id) || this.areAllPaAgreementsViewOnly()
      );
    }
    return false;
  }

  isExhausted() {
    if (this.searchParameters.premiumaccess) {
      const id = this.searchParameters.premiumaccess.split(':')[1];
      return (
        this.isPaAgreementExhausted(id) || this.areAllPaAgreementsExhausted()
      );
    }
    return false;
  }

  isPaAgreementViewOnly(paAgreementId) {
    const agreements = this.searchProfile.fullAgreementData.PA;
    if (!agreements) {
      return false;
    }
    for (const agreement of agreements) {
      if (agreement.Id && agreement.Id.toString() === paAgreementId) {
        return agreement.EffectivePermissions.indexOf('Download') < 0;
      }
    }
    return false;
  }

  areAllPaAgreementsViewOnly() {
    const agreements = this.searchProfile.fullAgreementData.PA;
    if (!agreements) {
      return false;
    }

    for (const agreement of agreements) {
      if (agreement.EffectivePermissions.indexOf('Download') > -1) {
        return false;
      }
    }

    return true;
  }

  isPaAgreementExhausted(paAgreementId) {
    const agreements = this.searchProfile.fullAgreementData.PA;
    if (!agreements) {
      return false;
    }

    for (const agreement of agreements) {
      if (agreement.Id && agreement.Id.toString() === paAgreementId) {
        return agreement.IsExhausted;
      }
    }
    return false;
  }

  areAllPaAgreementsExhausted() {
    const agreements = this.searchProfile.fullAgreementData.PA;
    if (!agreements) {
      return false;
    }

    for (const agreement of agreements) {
      if (!agreement.IsExhausted) {
        return false;
      }
    }
    return true;
  }

  showEmptyResults() {
    return !this.totalResults > 0 && !this.isRecentlyViewedImages();
  }

  selectedPAId() {
    if (this.hasPremiumAccess()) {
      return this.searchParameters.premiumaccess.split(':')[1];
    }
    return null;
  }

  // NATURAL LANGAUGE SEARCH METHODS
  isCreativeNLSAvailable() {
    return (
      (this.isCreativeOnly() || (this.isFilm() && this.isBothFamilies())) &&
      !this.isSpecialSearch()
    );
  }

  isEnhancedSearch() {
    return this.phraseProcessing.naturalLanguage;
  }

  isPhraseProcessingOff() {
    return this.searchParameters.phraseprocessing === 'excludenaturallanguage';
  }

  isSortedBestMatch() {
    return this.searchParameters.sortorder === 'best';
  }

  isSortedNewest() {
    return this.searchParameters.sortorder === 'newest';
  }

  isSortedOldest() {
    return this.searchParameters.sortorder === 'oldest';
  }

  isSortedMostPopular() {
    return this.searchParameters.sortorder === 'mostpopular';
  }
}
